// SPDX-License-Identifier: MIT
// /tarifs — 5-plan pricing page (spec 2026-05-24-genesis-user-flow-freemium-paid-design).
// Gratuit / Packs d'idées / Desktop à vie / Cloud (featured) / Entreprise.
// Presentational: no Stripe this session — the Cloud CTA reveals a "bientôt" notice,
// Entreprise is a mailto.
import { t, tList } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import { renderHeader } from '../components/header';
import { renderFooter } from '../components/footer';
import { wireCheckoutCta } from '../checkout/cta';
import type { Locale } from '../i18n/detect';

export interface TarifsPageOptions {
  locale?: Locale;
  onBack: () => void; // → /welcome
  // Authed chrome (best-effort) : when set, the shared header shows the ✦ idées
  // pill + account menu — so a signed-in visitor keeps the same chrome here as
  // on /welcome & /compte. Anon → omitted, header stays decorative.
  user?: { email: string };
  onSignOut?: () => void | Promise<void>;
}

const ENTERPRISE_CONTACT = 'contact@myconciergerie.fr';

export function renderTarifsPage(opts: TarifsPageOptions): DocumentFragment {
  const frag = document.createDocumentFragment();
  frag.appendChild(renderHeader({ locale: opts.locale, activePath: '/tarifs', user: opts.user, onSignOut: opts.onSignOut }));

  const main = document.createElement('main');
  main.className = 'tarifs';
  main.setAttribute('data-component', 'tarifs-page');

  const items = (key: string) => tList(key).map((i) => `<li>${escapeHtml(i)}</li>`).join('');
  const mailto = `mailto:${ENTERPRISE_CONTACT}?subject=${encodeURIComponent('Genesis Entreprise')}`;

  main.innerHTML = `
    <button type="button" data-component="tarifs-back" class="tarifs__back">${escapeHtml(t('tarifs.backToApp'))}</button>
    <h1 class="tarifs__title">${escapeHtml(t('tarifs.pageTitle'))}</h1>
    <p class="tarifs__lead">${escapeHtml(t('tarifs.lead'))}</p>
    <div class="plans">
      <div class="plan">
        <div class="plan__name">${escapeHtml(t('tarifs.free.name'))}</div>
        <div class="plan__price">${escapeHtml(t('tarifs.free.price'))}</div>
        <div class="plan__sub">${escapeHtml(t('tarifs.free.sub'))}</div>
        <ul class="plan__list">${items('tarifs.free.items')}</ul>
        <button type="button" data-component="tarifs-free-cta" class="plan__cta plan__cta--free">${escapeHtml(t('tarifs.free.cta'))}</button>
      </div>
      <div class="plan">
        <div class="plan__name">${escapeHtml(t('tarifs.packs.name'))}</div>
        <div class="plan__price">${escapeHtml(t('tarifs.packs.price'))}</div>
        <div class="plan__sub">${escapeHtml(t('tarifs.packs.sub'))}</div>
        <ul class="plan__list">${items('tarifs.packs.items')}</ul>
        <button type="button" data-component="tarifs-packs-cta" class="plan__cta plan__cta--ghost">${escapeHtml(t('tarifs.packs.cta'))}</button>
      </div>
      <div class="plan">
        <div class="plan__name">${escapeHtml(t('tarifs.lifetime.name'))}</div>
        <div class="plan__price">${escapeHtml(t('tarifs.lifetime.price'))}<small>${escapeHtml(t('tarifs.lifetime.per'))}</small></div>
        <div class="plan__sub">${escapeHtml(t('tarifs.lifetime.sub'))}</div>
        <ul class="plan__list">${items('tarifs.lifetime.items')}</ul>
        <button type="button" data-component="tarifs-lifetime-cta" class="plan__cta plan__cta--ghost">${escapeHtml(t('tarifs.lifetime.cta'))}</button>
      </div>
      <div class="plan plan--feat">
        <div class="plan__name">${escapeHtml(t('tarifs.cloud.name'))}</div>
        <div class="plan__price">${escapeHtml(t('tarifs.cloud.price'))}<small>${escapeHtml(t('tarifs.cloud.per'))}</small></div>
        <div class="plan__sub">${escapeHtml(t('tarifs.cloud.sub'))}</div>
        <ul class="plan__list">${items('tarifs.cloud.items')}</ul>
        <button type="button" data-component="tarifs-cloud-cta" class="plan__cta plan__cta--pro">${escapeHtml(t('tarifs.cloud.cta'))}</button>
        <p class="tarifs__pro-soon" data-component="tarifs-cloud-soon" hidden>${escapeHtml(t('tarifs.cloudSoon'))}</p>
      </div>
      <div class="plan">
        <span class="plan__badge-sov">${escapeHtml(t('tarifs.ent.badge'))}</span>
        <div class="plan__name">${escapeHtml(t('tarifs.ent.name'))}</div>
        <div class="plan__price plan__price--quote">${escapeHtml(t('tarifs.ent.price'))}</div>
        <div class="plan__sub">${escapeHtml(t('tarifs.ent.sub'))}</div>
        <ul class="plan__list">${items('tarifs.ent.items')}</ul>
        <a data-component="tarifs-ent-cta" class="plan__cta plan__cta--ent" href="${mailto}">${escapeHtml(t('tarifs.ent.cta'))}</a>
      </div>
    </div>
  `;
  frag.appendChild(main);
  frag.appendChild(renderFooter());

  main.querySelector<HTMLButtonElement>('[data-component="tarifs-back"]')!
    .addEventListener('click', () => opts.onBack());
  main.querySelector<HTMLButtonElement>('[data-component="tarifs-free-cta"]')!
    .addEventListener('click', () => opts.onBack());
  main.querySelector<HTMLButtonElement>('[data-component="tarifs-cloud-cta"]')!
    .addEventListener('click', () => {
      main.querySelector<HTMLElement>('[data-component="tarifs-cloud-soon"]')!.hidden = false;
    });

  // CP-2 checkout (Mollie, test mode) — one-time SKUs. Cloud stays "bientôt".
  wireCheckoutCta(main.querySelector<HTMLButtonElement>('[data-component="tarifs-packs-cta"]')!, 'pack_web');
  wireCheckoutCta(main.querySelector<HTMLButtonElement>('[data-component="tarifs-lifetime-cta"]')!, 'desktop_lifetime');

  return frag;
}
